import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { aiCommandOutput, aiSession, aiSessionCommands, type AiScope, type AiSessionCommand } from '../api/assistant'
import { actOnDraft, changesPanelQuery, type Draft } from '../api/changes'
import { ApiError } from '../api/client'
import './liveCommands.css'

/**
 * "Live commands" - partea din dreapta a lui Adam MARIT (`is-wide`).
 *
 * Ce a facut discutia de acum, in ordine, cel mai nou jos:
 *
 *  - fiecare unealta, din fluxul SSE (`step` inainte, `step_end` dupa - vezi
 *    `DirectBrain::stepFields()`): nume, ora, durata;
 *  - comenzile pe masini (run_command / remediate / propose_change): textul
 *    EXACT al comenzii, masina cu legatura la fisa, starea, si rezultatul
 *    intr-o caseta de terminal;
 *  - propunerile care asteapta aprobare, cu Approve / Deny pe capetele pe care
 *    le are deja ecranul Change Proposals (`/changes/{id}/approve|reject`).
 *    Cand omul n-are drept la ele (403), un drum catre locul unde se aproba.
 *
 * TOT CE E AICI E TEXT DE LA MASINI SI DE LA MODEL: se scrie ca text (React il
 * scapa), niciodata ca HTML. Si nu se cere nimic ce sesiunea omului nu vede
 * deja: aceleasi capete ca sertarul (`sessionCommands`, `commandOutput`,
 * `changes`), cu verificarile lor pe server.
 *
 * Reimprospatarea: la ~3 s cat e ceva in lucru (o intrebare care curge, o
 * unealta neterminata, o comanda trimisa sau care ruleaza, o propunere care
 * ruleaza). Se opreste singura cand nu mai e nimic, si cand panoul e ascuns -
 * atunci componenta nici nu e montata.
 */

/** Uneltele care ating masinile - aceeasi lista ca `DirectBrain::MACHINE_TOOLS`. */
export const MACHINE_TOOLS = ['run_command', 'remediate', 'propose_change']

export const POLL_MS = 3000

/** Un pas din flux, tinut de sertar pentru discutia de acum. */
export interface LiveStep {
  key: number
  tool: string
  /** Cand a pornit, in ms (ceasul browserului). */
  at: number
  done: boolean
  ms?: number
  command?: string
  deviceId?: number
  commandId?: number
  draftId?: number
  error?: boolean
}

export type LiveFilter = 'all' | 'machine' | 'errors'

export type LiveState = 'working' | 'done' | 'sent' | 'running' | 'ok' | 'error' | 'canceled' | 'proposed' | 'approved' | 'stopped' | 'rejected' | 'unknown'

export interface LiveRow {
  key: string
  kind: 'tool' | 'command' | 'proposal'
  tool: string
  /** Pentru ordonare: ms. */
  when: number
  /** Ora aratata. */
  clock: string
  ms?: number
  command?: string
  deviceId?: number
  deviceName?: string
  state: LiveState
  output?: string
  commandId?: number
  draftId?: number
  title?: string
}

/** Ce fac cu starea din `Commands\Result::decode()`. */
const COMMAND_STATE: Record<string, LiveState> = {
  sent: 'sent',
  waiting: 'running',
  ok: 'ok',
  error: 'error',
  canceled: 'canceled',
}

const STATE_LABEL: Record<LiveState, string> = {
  working: 'running',
  done: 'done',
  sent: 'sent',
  running: 'running',
  ok: 'OK',
  error: 'error',
  canceled: 'canceled',
  proposed: 'waiting for approval',
  approved: 'approved',
  stopped: 'stopped',
  rejected: 'denied',
  unknown: 'see Change Proposals',
}

const IN_WORK: LiveState[] = ['working', 'sent', 'running', 'approved']

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function clockOf(ms: number): string {
  const d = new Date(ms)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/** `2026-09-30 14:02:11` (ora serverului, ca in restul portalului) -> ms. */
function serverMs(at: string): number {
  const t = Date.parse(at.replace(' ', 'T'))
  return Number.isNaN(t) ? 0 : t
}

function serverClock(at: string): string {
  const m = at.match(/(\d{2}:\d{2}(?::\d{2})?)/)
  return m ? m[1]! : at
}

export function duration(ms: number | undefined): string {
  if (ms === undefined) return ''
  if (ms < 1000) return `${ms} ms`
  const s = ms / 1000
  return s < 60 ? `${s.toFixed(1)} s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`
}

function draftState(status: Draft['status']): LiveState {
  if (status === 'running') return 'running'
  if (status === 'done') return 'ok'
  return status
}

/**
 * Randurile panoului: pasii din flux, imbogatiti cu randul de comanda sau
 * propunerea pe care le-au lasat, plus comenzile si propunerile discutiei
 * venite din sesiune fara pas vazut aici (de dinainte de ridicarea panoului).
 * In ordinea timpului, cel mai nou jos.
 */
export function liveRows(
  steps: LiveStep[],
  commands: AiSessionCommand[],
  outputs: Record<number, { state: string; output: string }>,
  drafts: Draft[],
  /** Propunerile discutiei cunoscute doar ca numar (fara drept la ecranul lor). */
  draftIds: number[] = [],
): LiveRow[] {
  const byCommand = new Map(commands.map((c) => [c.id, c]))
  const byDraft = new Map(drafts.map((d) => [d.ID, d]))
  const usedCommands = new Set<number>()
  const usedDrafts = new Set<number>()
  const rows: LiveRow[] = []

  for (const step of steps) {
    const machine = MACHINE_TOOLS.includes(step.tool)
    const row: LiveRow = {
      key: `s${step.key}`,
      kind: machine ? 'command' : 'tool',
      tool: step.tool,
      when: step.at,
      clock: clockOf(step.at),
      ms: step.ms,
      command: step.command,
      deviceId: step.deviceId,
      state: step.error ? 'error' : step.done ? (machine ? 'sent' : 'done') : 'working',
    }
    if (step.commandId) {
      row.commandId = step.commandId
      const c = byCommand.get(step.commandId)
      const live = outputs[step.commandId]
      if (c) {
        usedCommands.add(c.id)
        row.command = c.command
        row.deviceId = c.device_id
        row.deviceName = c.device
        row.state = COMMAND_STATE[c.state] ?? row.state
        row.output = c.output
      } else if (live) {
        row.state = COMMAND_STATE[live.state] ?? row.state
        row.output = live.output
      }
    }
    if (step.draftId) {
      row.kind = 'proposal'
      row.draftId = step.draftId
      const d = byDraft.get(step.draftId)
      if (d) {
        usedDrafts.add(d.ID)
        row.title = d.title
        row.command = d.command || row.command
        row.state = draftState(d.status)
        const first = d.device_list[0]
        if (first) {
          row.deviceId = first.id
          row.deviceName = first.name
        }
      } else if (!step.error) {
        row.state = step.tool === 'propose_change' ? 'proposed' : 'approved'
      }
    }
    rows.push(row)
  }

  for (const c of commands) {
    if (usedCommands.has(c.id)) continue
    rows.push({
      key: `c${c.id}`,
      kind: 'command',
      tool: 'command',
      when: serverMs(c.at),
      clock: serverClock(c.at),
      command: c.command,
      deviceId: c.device_id,
      deviceName: c.device,
      state: COMMAND_STATE[c.state] ?? 'sent',
      output: c.output,
      commandId: c.id,
    })
  }

  for (const d of drafts) {
    if (usedDrafts.has(d.ID)) continue
    const first = d.device_list[0]
    rows.push({
      key: `d${d.ID}`,
      kind: 'proposal',
      tool: d.mode === 'incident' ? 'remediate' : 'propose_change',
      when: serverMs(d.created_at),
      clock: serverClock(d.created_at),
      command: d.command,
      title: d.title,
      deviceId: first?.id,
      deviceName: first?.name,
      state: draftState(d.status),
      draftId: d.ID,
    })
  }

  const stepDrafts = new Set(steps.map((s) => s.draftId ?? 0))
  for (const id of draftIds) {
    if (byDraft.has(id) || stepDrafts.has(id)) continue
    rows.push({ key: `d${id}`, kind: 'proposal', tool: 'propose_change', when: 0, clock: '', title: `Change proposal #${id}`, state: 'unknown', draftId: id })
  }

  /* Stabil: la timp egal ramane ordinea de mai sus (pasii in ordinea lor). */
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => a.r.when - b.r.when || a.i - b.i)
    .map((x) => x.r)
}

export function filterRows(rows: LiveRow[], filter: LiveFilter): LiveRow[] {
  if (filter === 'machine') return rows.filter((r) => r.kind !== 'tool')
  if (filter === 'errors') return rows.filter((r) => r.state === 'error')
  return rows
}

export function inWork(busy: boolean, rows: LiveRow[], drafts: Draft[]): boolean {
  return busy || rows.some((r) => IN_WORK.includes(r.state)) || drafts.some((d) => d.status === 'approved' || d.status === 'running')
}

export interface LiveCommandsProps {
  scope: AiScope
  steps: LiveStep[]
  busy: boolean
  /** Creste la fiecare `step_end` si la sfarsitul unei intrebari: citeste din nou. */
  pulse: number
  style?: CSSProperties
}

export function LiveCommands({ scope, steps, busy, pulse, style }: LiveCommandsProps) {
  const client = useQueryClient()
  const [filter, setFilter] = useState<LiveFilter>('all')
  const [commands, setCommands] = useState<AiSessionCommand[]>([])
  const [outputs, setOutputs] = useState<Record<number, { state: string; output: string }>>({})
  const [sessionDrafts, setSessionDrafts] = useState<number[]>([])
  const [folded, setFolded] = useState<Record<string, boolean>>({})
  const [armed, setArmed] = useState<number | null>(null)
  const [acting, setActing] = useState(false)
  const [draftMsg, setDraftMsg] = useState<Record<number, string>>({})
  const list = useRef<HTMLDivElement>(null)
  const stuck = useRef(true)

  const draftIds = useMemo(() => {
    const all = new Set<number>(sessionDrafts)
    for (const s of steps) if (s.draftId) all.add(s.draftId)
    return [...all].sort((a, b) => a - b)
  }, [sessionDrafts, steps])

  const changes = useQuery({
    ...changesPanelQuery(draftIds),
    enabled: draftIds.length > 0,
  })
  const canAct = !(changes.error instanceof ApiError && changes.error.status === 403)
  const drafts = useMemo(
    () => (draftIds.length > 0 && changes.data ? changes.data.drafts.filter((d) => draftIds.includes(d.ID)) : []),
    [changes.data, draftIds],
  )

  const rows = useMemo(() => liveRows(steps, commands, outputs, drafts, draftIds), [steps, commands, outputs, drafts, draftIds])
  const shown = filterRows(rows, filter)
  const working = inWork(busy, rows, drafts)

  /* Pasii fara rand in sesiune (discutia inca ruleaza, deci nu s-a scris):
     starea lor vine din `commandOutput`, pe care il are si sertarul. */
  const stepsRef = useRef(steps)
  stepsRef.current = steps
  const scopeRef = useRef(scope)
  scopeRef.current = scope

  const refresh = useCallback(async () => {
    const at = scopeRef.current
    let known: AiSessionCommand[] = []
    try {
      known = (await aiSessionCommands(at)).rows ?? []
      setCommands(known)
    } catch {
      /* ce era ramane */
    }
    try {
      setSessionDrafts((await aiSession(at)).changes ?? [])
    } catch {
      /* ce era ramane */
    }
    const have = new Set(known.map((c) => c.id))
    const pending = stepsRef.current.filter((s) => s.commandId && !have.has(s.commandId)).map((s) => s.commandId!)
    for (const id of pending) {
      try {
        const out = await aiCommandOutput(id)
        if (out.ok)
          setOutputs((cur) => ({
            ...cur,
            [id]: { state: out.state ?? 'sent', output: out.output ?? '' },
          }))
      } catch {
        /* ramane cum era */
      }
    }
  }, [])

  /* O citire la montare, la fiecare `step_end` si cand se schimba discutia. */
  useEffect(() => {
    void refresh()
  }, [refresh, pulse, scope.conversation_id, scope.device_id, scope.scope])

  /* Cat e ceva in lucru: din 3 in 3 secunde. Altfel, liniste. */
  useEffect(() => {
    if (!working) return
    const id = window.setInterval(() => {
      void refresh()
      if (draftIds.length > 0) void changes.refetch()
    }, POLL_MS)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [working, refresh, draftIds.join(',')])

  /* Derularea: jos, cat omul n-a urcat singur sa citeasca ceva. */
  const onScroll = () => {
    const el = list.current
    if (!el) return
    stuck.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24
  }
  useEffect(() => {
    const el = list.current
    if (el && stuck.current) el.scrollTop = el.scrollHeight
  }, [shown.length, rows])

  const lastCommand = [...shown].reverse().find((r) => r.output !== undefined && r.output !== '')?.key

  const act = async (draft: number, action: 'approve' | 'reject') => {
    if (acting) return
    if (action === 'approve' && armed !== draft) {
      setArmed(draft)
      window.setTimeout(() => setArmed((a) => (a === draft ? null : a)), 4000)
      return
    }
    setArmed(null)
    setActing(true)
    try {
      const r = await actOnDraft(draft, action)
      setDraftMsg((m) => ({
        ...m,
        [draft]: r.ok === false ? r.error || 'Failed.' : '',
      }))
      void client.invalidateQueries({ queryKey: ['changes'] })
    } catch (e) {
      setDraftMsg((m) => ({
        ...m,
        [draft]: e instanceof ApiError ? e.message : 'The request failed.',
      }))
    } finally {
      setActing(false)
    }
  }

  return (
    <section className="od-ai-live" id="odAiLive" aria-label="Live commands" style={style}>
      <div className="od-ai-live-head">
        <strong>Live commands</strong>
        {working ? <span className="od-ai-live-pulse" title="Refreshing every 3 seconds" aria-label="live" /> : null}
        <span className="od-ai-live-gap" />
        <a className="od-ai-live-all" href="/app/assistant/activity">
          Open full history
        </a>
      </div>
      <div className="od-ai-live-filters" role="group" aria-label="Filter">
        {(
          [
            ['all', 'All'],
            ['machine', 'Machine commands'],
            ['errors', 'Errors'],
          ] as [LiveFilter, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`od-ai-live-filter${filter === key ? ' is-active' : ''}`}
            data-filter={key}
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="od-ai-live-list" ref={list} onScroll={onScroll}>
        {shown.length === 0 ? (
          <p className="od-ai-live-empty">
            {rows.length === 0
              ? 'Nothing ran in this conversation yet. Tools and machine commands show up here as they happen.'
              : 'Nothing matches this filter.'}
          </p>
        ) : null}
        {shown.map((r) => {
          const open = folded[r.key] === undefined ? r.key === lastCommand || r.state === 'error' : !folded[r.key]
          return (
            <div key={r.key} className={`od-ai-live-row kind-${r.kind} state-${r.state}`} data-key={r.key}>
              <div className="od-ai-live-line">
                <span className="od-ai-live-clock">{r.clock}</span>
                <code className="od-ai-live-tool">{r.tool}</code>
                {r.ms !== undefined ? <span className="od-ai-live-ms">{duration(r.ms)}</span> : null}
                <span className="od-ai-live-gap" />
                <span className={`od-ai-live-state is-${r.state}`}>{STATE_LABEL[r.state]}</span>
              </div>
              {r.title ? <div className="od-ai-live-title">{r.title}</div> : null}
              {r.command ? <pre className="od-ai-live-cmd">{r.command}</pre> : null}
              {r.deviceId ? (
                <div className="od-ai-live-device">
                  on{' '}
                  <a href={`/app/inventoree/device/${r.deviceId}`} className="od-ai-live-device-link">
                    {r.deviceName || `#${r.deviceId}`}
                  </a>
                </div>
              ) : null}
              {r.kind === 'proposal' && r.draftId && (r.state === 'proposed' || r.state === 'unknown') ? (
                <div className="od-ai-live-approve">
                  {canAct && changes.data && r.state === 'proposed' ? (
                    <>
                      <button type="button" className="od-ai-live-yes" disabled={acting} onClick={() => void act(r.draftId!, 'approve')}>
                        {armed === r.draftId ? 'Sure?' : 'Approve'}
                      </button>
                      <button type="button" className="od-ai-live-no" disabled={acting} onClick={() => void act(r.draftId!, 'reject')}>
                        Deny
                      </button>
                    </>
                  ) : (
                    <a href="/app/change">Approve or deny in Change Proposals</a>
                  )}
                  {draftMsg[r.draftId] ? <span className="od-ai-live-msg">{draftMsg[r.draftId]}</span> : null}
                </div>
              ) : null}
              {r.output !== undefined && r.output !== '' ? (
                <div className="od-ai-live-out">
                  <button type="button" className="od-ai-live-fold" aria-expanded={open} onClick={() => setFolded((f) => ({ ...f, [r.key]: open }))}>
                    {open ? 'Hide result' : 'Show result'}
                  </button>
                  {open ? <pre className="od-ai-live-term">{r.output}</pre> : null}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}
